// Formatting helpers.

const jpyFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

/** Formats a JPY amount. A base price of 0 is treated as "price on request". */
export function formatPrice(amountJpy: number): string {
  if (!amountJpy) return 'On request';
  return jpyFormatter.format(amountJpy);
}

export function formatJpy(amountJpy: number): string {
  return jpyFormatter.format(amountJpy);
}

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

/** Formats an ISO timestamp as e.g. "14 Aug 2026". Empty input gives '—'. */
export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateFormatter.format(date);
}

const calendarDateFormatter = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

/**
 * Formats a calendar date (`yyyy-mm-dd`) as e.g. "Sat, 14 Aug 2026".
 *
 * Distinct from `formatDate` in two ways, both because the input is a day rather
 * than an instant: it is parsed at noon so the rendered day cannot slide by one
 * for a reader west of UTC, and it carries the weekday — every date on a booking
 * request is a day someone has to staff.
 */
export function formatCalendarDate(date: string | undefined): string {
  if (!date) return '—';
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return calendarDateFormatter.format(parsed);
}

const monthYearFormatter = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'long',
});

/**
 * A calendar date (`yyyy-mm-dd`) as just its month, e.g. "March 2018". Empty
 * input, or a date that cannot be read, gives ''.
 *
 * For dates whose day is beside the point — when a tour leader started guiding,
 * say. Parsed at noon for the same reason `formatCalendarDate` is: at midnight
 * UTC a 1st slides back into the previous month for a reader west of it, which
 * here would cost a whole month.
 */
export function formatMonthYear(date: string | undefined): string {
  if (!date) return '';
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return monthYearFormatter.format(parsed);
}

/**
 * A calendar date (`yyyy-mm-dd`) as a `Date` in the reader's own timezone, or
 * undefined when it cannot be read.
 *
 * The pair to `toCalendarDate`, and the two exist only for date pickers, which
 * speak `Date` while everything stored here is a `yyyy-mm-dd` string. Built from
 * local parts rather than parsed from the string, because `new Date('2026-08-14')`
 * is midnight *UTC* — which is the 13th in London and shows the wrong day
 * highlighted in the calendar.
 */
export function parseCalendarDate(date: string | undefined): Date | undefined {
  if (!date) return undefined;
  const parts = date.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return undefined;
  const [year, month, day] = parts;
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** A `Date` as the calendar day it is in the reader's timezone, `yyyy-mm-dd`. */
export function toCalendarDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Today, as the reader's own calendar day.
 *
 * The browser's day rather than Tokyo's, deliberately: it is what a date input
 * offers as "today" and what the concierge's own calendar says, and the two must
 * agree or the earliest selectable date looks wrong. Where the difference
 * matters — refusing a date in the past — the server decides, and allows a day
 * of slack for exactly this reason (see `validateBookingRequest`).
 */
export function todayCalendarDate(): string {
  return toCalendarDate(new Date());
}

const jstDeadlineFormatter = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Tokyo',
});

/**
 * Formats a hold deadline in JST, e.g. "Sat, 14 Aug, 17:00".
 *
 * Pinned to Tokyo rather than shown in the reader's own zone for the same reason
 * the emails are: the deadline was quoted to a partner in Japan, and a concierge
 * checking it from a laptop still set to London must not be shown a different
 * hour than the one in their inbox.
 */
export function formatDeadlineJst(iso: string | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return jstDeadlineFormatter.format(parsed);
}

const jstDayFormatter = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Tokyo',
});

/**
 * Formats a free-cancellation deadline as the day it falls on, in JST, e.g.
 * "Thu, 3 Sep 2026".
 *
 * A day rather than an instant because the policy is a number of days: the
 * deadline is the end of that day, and quoting "23:59" invites a concierge to
 * read a whole-day promise as a race against a clock. Still resolved in Tokyo —
 * the last hours of a Japanese day are the previous day in London, and the date
 * shown must be the one the artisan's diary is kept in.
 */
export function formatDeadlineDayJst(iso: string | undefined): string {
  if (!iso) return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return jstDayFormatter.format(parsed);
}
